import { Fragment, useState, useRef } from "react";
import { Transition } from "@headlessui/react";
import Link from "next/link";
import type { FC } from "react";
import IsRtl from "components/Shared/isRtl";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { useRouter } from "next/router";

interface LanguageOption {
  label: string;
  href: string;
  unsupportedText?: string;
}

interface LanguageDropdownProps {
  currentLocale: string;
  labels: {
    trigger: string;
    options: LanguageOption[];
  };
  unSupportedLocales: string[];
}

const LanguagesDropdown: FC<LanguageDropdownProps> = ({
  currentLocale,
  labels,
  unSupportedLocales,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const isRtl = IsRtl();

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (menuRef.current && e.target === e.currentTarget) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative font-effra inline-block w-full lg:w-fit text-left" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`!font-bold group relative text-sm xl:text-base border-b border-black lg:border-0 px-2 pt-5 pb-[16px] w-full xl:py-6 flex items-center gap-1 transition-colors
          ${!isOpen ? "lg:hover:bg-neutral-200" : ""}
          ${isOpen ? "bg-neutral-200 lg:bg-oldTheme-secondary lg:!text-white" : "!text-black"}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {labels.trigger}
        <span className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
          <ChevronDownIcon width={28} height={28} color="currentColor" aria-hidden={true} />
        </span>
      </button>

      {isOpen && (
        <>
          <div
            onClick={onOverlayClick}
            className="hidden lg:fixed inset-0 z-40 bg-transparent"
            aria-hidden="true"
          />

          <Transition
            as={Fragment}
            show={isOpen}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <div
              className={`relative lg:absolute mt-1 ${
                isRtl ? "origin-top-left left-0" : "origin-top-right right-0"
              } lg:shadow-md w-[90vw] lg:w-48 lg:bg-white text-black focus:outline-none z-50`}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="language-menu-button"
            >
              <div className="flex flex-col gap-0">
                {labels.options.map((opt, index) => {
                  const localeCode = opt.href.replace("/", "");
                  const isUnsupported = unSupportedLocales.includes(localeCode);
                  const isCurrent = currentLocale === localeCode;

                  if (isUnsupported) {
                    return (
                      <div
                        key={index}
                        className={`group text-start text-sm xl:text-base font-medium p-3 w-full flex items-center justify-between transition-colors cursor-default
                          ${isCurrent ? "text-white bg-oldTheme-primary font-semibold" : ""}
                        `}
                        role="menuitem"
                        aria-disabled
                      >
                        <span className="opacity-30">{opt.label}</span>
                        {opt.unsupportedText && (
                          <div className="relative ml-2 group">
                            <HiOutlineExclamationCircle size={18} className="text-red-500" aria-hidden="true" />
                            <Transition
                              as="div"
                              show
                              enter="transition ease-out duration-200"
                              enterFrom="opacity-0 translate-y-1"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition ease-in duration-150"
                              leaveFrom="opacity-100 translate-y-0"
                              leaveTo="opacity-0 translate-y-1"
                            >
                              <div
                                className={` absolute z-50 ${
                                  isRtl ? "left-full" : "right-full"
                                } top-6 w-max max-w-[200px] bg-white text-red-600 text-xs font-medium px-3 py-2 shadow-[0_0_15px_rgba(0,0,0,0.1)] hidden group-hover:block whitespace-normal`}
                              >
                                {opt.unsupportedText}
                              </div>
                            </Transition>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link key={index} href={router.asPath} locale={localeCode} passHref>
                      <a
                        onClick={() => setIsOpen(false)}
                        className={`text-start text-sm xl:text-base font-medium p-3 w-full
                          transition-colors cursor-pointer flex items-center justify-between
                          ${
                            isCurrent
                              ? "text-white bg-oldTheme-primary font-semibold"
                              : "hover:bg-neutral-200 lg:hover:bg-neutral-100"
                          }
                        `}
                        role="menuitem"
                      >
                        {opt.label}
                      </a>
                    </Link>
                  );
                })}
              </div>
            </div>
          </Transition>
        </>
      )}
    </div>
  );
};

export default LanguagesDropdown;
